import { useShortcutStore } from "@/stores/shortcut-store";
import { ShortcutKeyCaps } from "@/components/ui";
import { CONCEPT_ICONS } from "@/components/ui/concept-icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { surfaceHoverClassName } from "@/components/ui/surface-contract";
import { emitDesktopAction } from "@/lib/desktop-action-events";
import { isMacPlatform } from "@/lib/os-platform";
import { shortcutKeys } from "@/lib/shortcuts";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { panelsHeaderMessages } from "@/i18n/messages/panels-header";

interface UserProfileMenuProps {
  userLetter: string;
  onSettings?: () => void;
  className?: string;
  align?: "start" | "center" | "end";
  side?: "top" | "right" | "bottom" | "left";
  /** Controlled open state. Omit for uncontrolled (self-managed) behavior. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  "data-testid"?: string;
}

export function UserProfileMenu({
  userLetter,
  onSettings,
  className,
  align = "start",
  side = "bottom",
  open,
  onOpenChange,
  "data-testid": testId = "space-profile-menu",
}: UserProfileMenuProps) {
  const t = useT(panelsHeaderMessages);
  useShortcutStore((state) => state.overrides);
  const continuousVoiceKeys = shortcutKeys("shortcut-continuous-voice");
  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            surfaceHoverClassName(),
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-transparent bg-transparent text-appkit-subhead font-semibold text-muted outline-none transition-[transform,color] hover:scale-[1.02] hover:text-primary focus-visible:ring-2",
            className,
          )}
          data-testid={testId}
          aria-label={t("profileMenu")}
          data-interactive="true"
        >
          <span>{userLetter}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        side={side}
        className="min-w-60"
        data-testid={`${testId}-content`}
        data-interactive="true"
      >
        <DropdownMenuItem
          onClick={() => void emitDesktopAction("toggle-workspace")}
          data-testid={`${testId}-toggle-mode`}
        >
          <CONCEPT_ICONS.workspace className="h-3.5 w-3.5" />
          <span>{t("toggleMode")}</span>
          <ShortcutKeyCaps
            keys={shortcutKeys("shortcut-toggle-workspace")}
            variant="raised"
            className="ml-auto pl-4"
          />
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => void emitDesktopAction("text-chat")}
          data-testid={`${testId}-text-chat`}
        >
          <CONCEPT_ICONS.chat className="h-3.5 w-3.5" />
          <span>{t("textChat")}</span>
          <ShortcutKeyCaps
            keys={shortcutKeys("shortcut-input-bar")}
            variant="raised"
            className="ml-auto pl-4"
          />
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => void emitDesktopAction("voice-chat")}
          data-testid={`${testId}-voice-chat`}
        >
          <CONCEPT_ICONS.voice className="h-3.5 w-3.5" />
          <span>{t("voiceChat")}</span>
          <ShortcutKeyCaps
            keys={shortcutKeys("shortcut-voice")}
            variant="raised"
            className="ml-auto pl-4"
          />
        </DropdownMenuItem>
        {continuousVoiceKeys.length > 0 ? (
          <DropdownMenuItem
            onClick={() => void emitDesktopAction("hands-free-voice")}
            data-testid={`${testId}-hands-free-voice`}
          >
            <CONCEPT_ICONS.voice className="h-3.5 w-3.5" />
            <span>{t("handsFreeVoice")}</span>
            <ShortcutKeyCaps
              keys={continuousVoiceKeys}
              variant="raised"
              className="ml-auto pl-4"
            />
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onSettings}
          data-testid={`${testId}-settings`}
        >
          <CONCEPT_ICONS.settings className="h-3.5 w-3.5" />
          <span>{t("settings")}</span>
          <ShortcutKeyCaps
            keys={isMacPlatform() ? ["⌘", ","] : ["Ctrl", ","]}
            variant="raised"
            className="ml-auto pl-4"
          />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export type { UserProfileMenuProps };
